/**
 * Free-element layer: body polygon (hit target), object name label, and — when
 * selected — the four resize edges, four corner handles, the rotation handle,
 * and the delete icon. Every affordance is projected through the element's
 * rotation, so a rotated element edits exactly like an axis-aligned one.
 *
 * A wall element draws like the perimeter walls: its thickness as a band
 * (the same fill and 1px ink lines) — one solid wall when it's thin, four
 * walls around a void when it's wide enough, the void showing the background
 * through the floor's cutout (PlanSvg). The whole footprint stays the hit target.
 */

import {
  CORNER_NAMES,
  EdgeName,
  FloorPlanRoom,
  freeElementRectangleCorners,
  freeElementRing,
  freeElementRotateVector,
  freeElementRotator,
  freeElementSideNormal,
  isKnownCustomAreaType,
  Point,
} from '@kaiizen/kai-floor-plan';
import { chamferData, EditorMutableState, perpCursor, ringsPath, showsElement, svgPoints, wallElementRings } from '@kaiizen/kai-floor-plan-editor-core';
import { fBody, ink, midtone, objectFill, roomStroke, selection, wallFill } from '../../styles';
import { ChamferCornerHandle, DragEdge, RotateHandle, TrashIcon } from './handles';
import { ALCOVE_BODY_STYLE } from './planCss';
import { usePlanExport } from './planExportContext';

export interface FreeElementsProps {
  st: EditorMutableState;
  s: FloorPlanRoom;
  sp: (p: Point) => Point;
  /** Object-name label size: 11px at a 750px stage minor axis, scaled with it. */
  labelFontPx: number;
  /** Draw object names (default). The assembler turns them off — many rooms of names is noise. */
  showObjectLabels?: boolean;
}

export function FreeElements({ st, s, sp, labelFontPx, showObjectLabels = true }: FreeElementsProps): React.JSX.Element {
  const exporting = usePlanExport();
  return (
    <>
      {(s.freeElements ?? []).map((r, i) => {
        if (!showsElement(st.layers, r)) return null; // hidden by the view menu; indices stay the room's
        const ring = freeElementRing(r).map(sp);
        const poly = svgPoints(ring);
        const active = (st.selection.kind === 'inner' || st.selection.kind === 'innerCorner') && st.selection.index === i;
        const stroke = active ? selection : midtone;
        const isWall = r.customAreaType === 'wall';
        const wallRings = isWall ? wallElementRings(r) : null;
        // Only flooring (an area-only floor retype) reads as a dashed region;
        // counters, walls, and objects are solid things → solid outline. Objects
        // fill --object-fill (the shared 2D/3D content blue) so they read as a
        // placed item, not a bare outline — and match the 3D view's objects.
        // Fill goes in inline `style` (not a `fill=` attr) so it beats the
        // `.alcove-body { fill: transparent }` CSS rule, which wins over attrs.
        // An element type from a newer version is drawn as an object, as in 3D.
        const isObject = r.customAreaType === 'object' || !isKnownCustomAreaType(r.customAreaType);
        const bodyStyle: React.CSSProperties = isObject
          ? { fill: objectFill, fillOpacity: 0.55, cursor: 'move' }
          : { cursor: 'move' };
        const c = sp({ x: r.x + r.width / 2, y: r.y + r.length / 2 });
        // Rect corners with rotation applied — the edges live where they render.
        const [cBL, cBR, cFR, cFL] = freeElementRectangleCorners(r).map(sp);
        const sideCursor = (side: EdgeName): string => perpCursor(freeElementSideNormal(r, side));
        // Chamfer math lives in the element's local frame; project its points
        // through the rotation before mapping to screen.
        const spr = (p: Point): Point => sp(freeElementRotator(r)(p));
        return (
          <g data-layer={r.customAreaType} key={i}>
            {
              /* A wall's visual is its band (outer face, inner face when hollow),
                drawn like the perimeter walls; the transparent hit polygon over
                it keeps the whole footprint as the move handle. */
            }
            {wallRings && (
              <path
                d={ringsPath([wallRings.outer, ...(wallRings.inner ? [wallRings.inner] : [])].map(points => points.map(sp)))}
                fill={wallFill}
                fillRule='evenodd'
                stroke={active ? selection : roomStroke}
                strokeWidth={active ? 2.5 : 1}
                pointerEvents='none'
              />
            )}
            {
              /* Same visible outline either way; the export copy drops the hit
                styling (transparent-fill css + handle + cursor) for a plain
                attribute-only element. */
            }
            {exporting
              ? (
                !isWall && (
                  <polygon
                    points={poly}
                    fill={isObject ? objectFill : 'none'}
                    fillOpacity={isObject ? 0.55 : undefined}
                    stroke={stroke}
                    strokeWidth={active ? 2.5 : 1.25}
                    strokeDasharray={r.customAreaType === 'flooring' ? '4 3' : undefined}
                  />
                )
              )
              : (
                <polygon
                  css={ALCOVE_BODY_STYLE}
                  data-handle={`inner:${i}`}
                  points={poly}
                  stroke={isWall ? 'none' : stroke}
                  strokeWidth={active ? 2.5 : 1.25}
                  strokeDasharray={r.customAreaType === 'flooring' ? '4 3' : undefined}
                  style={bodyStyle}
                />
              )}
            {isObject && showObjectLabels && !!r.name && (
              <text
                x={c.x}
                y={c.y}
                textAnchor='middle'
                dominantBaseline='central'
                pointerEvents='none'
                style={{ fontFamily: fBody, fontWeight: 700, fontSize: `${labelFontPx.toFixed(1)}px`, fill: ink }}
              >
                {r.name}
              </text>
            )}
            {active && (
              <>
                <DragEdge a={cBL} b={cBR} handle={`inner-edge:${i}:backEdge`} cursor={sideCursor('backEdge')} />
                <DragEdge a={cFL} b={cFR} handle={`inner-edge:${i}:frontEdge`} cursor={sideCursor('frontEdge')} />
                <DragEdge a={cBL} b={cFL} handle={`inner-edge:${i}:leftEdge`} cursor={sideCursor('leftEdge')} />
                <DragEdge a={cBR} b={cFR} handle={`inner-edge:${i}:rightEdge`} cursor={sideCursor('rightEdge')} />
                {CORNER_NAMES.map(which => {
                  const chamfer = chamferData(st, { kind: 'innerCorner', index: i, which })!;
                  return (
                    <ChamferCornerHandle
                      key={which}
                      chamfer={chamfer}
                      project={spr}
                      handle={`inner:${i}:${which}`}
                      selected={st.selection.kind === 'innerCorner' && st.selection.index === i && st.selection.which === which}
                      cursor={perpCursor(freeElementRotateVector(r, { x: chamfer.sx, y: chamfer.sy }))}
                      angle={r.rotation ?? 0}
                      diagonalSelected={false}
                    />
                  );
                })}
                {!st.readOnly && (
                  <RotateHandle
                    center={c}
                    rightSideMidpoint={spr({ x: r.x + r.width, y: r.y + r.length / 2 })}
                    outward={freeElementSideNormal(r, 'rightEdge')}
                    handle={`inner-rotate:${i}`}
                  />
                )}
              </>
            )}
            {active && <TrashIcon p={c} handle={`delinner:${i}`} />}
          </g>
        );
      })}
    </>
  );
}
